<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { INPUT_TYPES } from 'dashboard/components-next/taginput/helper/tagInputHelper.js';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import { searchContacts } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'single',
  },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const isSearching = ref(false);
const searchedContacts = ref([]);
const selectedContacts = ref([]);
const decoratedContacts = ref([]);

const { t } = useI18n();

const contactDecorate = contact => {
  return contact.name && contact.email
    ? `${contact.name} (${contact.email})`
    : contact.name || contact.email;
};

const handleBlur = e => emit('blur', e);
const handleInput = async searchValue => {
  isSearching.value = true;
  searchedContacts.value = [];
  if (!searchValue.trim()) {
    isSearching.value = false;
    searchedContacts.value = [];
    return;
  }

  try {
    searchedContacts.value = await searchContacts({
      keys: ['email'],
      query: searchValue,
    });
    isSearching.value = false;
  } catch (error) {
    useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
  } finally {
    isSearching.value = false;
  }
};

const handleSelectContact = contactItem => {
  if (props.mode === 'single') {
    selectedContacts.value = [contactItem.value];
  } else {
    selectedContacts.value = [...selectedContacts.value, contactItem.value];
  }
  selectedContacts.value = Array.from(
    new Set(selectedContacts.value.map(contact => JSON.stringify(contact)))
  ).map(contactStr => JSON.parse(contactStr));
  emit('update:modelValue', selectedContacts.value);

  decoratedContacts.value = selectedContacts.value.map(contact => {
    return typeof contact === 'string' ? contact : contactDecorate(contact);
  });
  searchedContacts.value = [];
};

const handleClearSelected = () => {
  searchedContacts.value = [];
  selectedContacts.value = [];
  decoratedContacts.value = [];
  emit('update:modelValue', selectedContacts.value);
};

const selectableContacts = computed(() => {
  const result = searchedContacts.value || [];
  return result.map(contact => ({
    value: contact,
    label: contactDecorate(contact),
  }));
});

watch(
  () => props.modelValue,
  newValue => {
    selectedContacts.value = newValue;
    decoratedContacts.value = selectedContacts.value.map(contact => {
      return typeof contact === 'string' ? contact : contactDecorate(contact);
    });
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex items-center flex-1 w-full gap-3 px-4 py-1 overflow-y-visible"
  >
    <div class="flex items-center w-full gap-3">
      <label
        v-if="label"
        class="w-[5rem] text-end mb-0.5 text-sm font-medium text-n-slate-11 whitespace-nowrap"
      >
        {{ label }}
      </label>
      <div class="flex-auto items-center tags-container">
        <TagInput
          v-model="decoratedContacts"
          allow-create
          focus-on-mount
          class="min-h-7"
          :mode="mode"
          :placeholder="placeholder"
          :menu-items="selectableContacts"
          :show-dropdown="selectableContacts.length > 0"
          :is-loading="isSearching"
          :disabled="disabled"
          :type="INPUT_TYPES.EMAIL"
          @input="handleInput"
          @blur="handleBlur"
          @remove="handleClearSelected"
          @add="handleSelectContact($event)"
        />
        <slot />
      </div>
    </div>
  </div>
</template>
